/**
 * AttendanceDashboard.jsx — Puantaj KPI Kartları
 * Rolüne göre farklı metrikler gösterir.
 * EMPLOYEE: kendi istatistikleri
 * MANAGER/HR: takım/şirket geneli
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  AlertTriangle,
  XCircle,
  Clock,
  CheckCircle,
  FileText,
  TrendingUp,
} from 'lucide-react';

const KpiCard = ({ icon: Icon, label, value, sub, variant = 'default', colorClass = '' }) => {
  const variants = {
    default: 'bg-white border-slate-100',
    success: 'bg-emerald-50 border-emerald-200',
    warning: 'bg-amber-50 border-amber-200',
    danger: 'bg-rose-50 border-rose-200',
    info: 'bg-sky-50 border-sky-200',
    primary: 'bg-indigo-50 border-indigo-200',
  };

  const iconColors = {
    default: 'text-slate-500',
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    danger: 'text-rose-600',
    info: 'text-sky-600',
    primary: 'text-indigo-600',
  };

  return (
    <div className={`rounded-2xl border p-5 shadow-sm transition-all hover:shadow-md ${variants[variant] || variants.default} ${colorClass}`}>
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            {label}
          </p>
          <div className="text-2xl font-black text-slate-800 tracking-tight">
            {value}
          </div>
          {sub && (
            <p className="text-xs font-medium text-slate-500">{sub}</p>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconColors[variant] || iconColors.default} bg-white/80`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
};

const AttendanceDashboard = ({ stats, role = 'EMPLOYEE', totalEmployees = 0 }) => {
  const { t } = useTranslation();

  if (role === 'EMPLOYEE') {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <KpiCard
          icon={CheckCircle}
          label={t('attendance_kpi_present', 'Devam Günü')}
          value={stats.present || 0}
          variant="success"
        />
        <KpiCard
          icon={AlertTriangle}
          label={t('attendance_kpi_late', 'Geç Kalma')}
          value={stats.late || 0}
          variant="warning"
        />
        <KpiCard
          icon={Clock}
          label={t('attendance_kpi_hours', 'Toplam Saat')}
          value={`${stats.totalHours || 0}h`}
          variant="primary"
        />
        <KpiCard
          icon={FileText}
          label={t('attendance_kpi_sick', 'Sağlık Raporu')}
          value={stats.sick || 0}
          variant="info"
        />
      </div>
    );
  }

  // MANAGER / HR / OWNER
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
      <KpiCard
        icon={Users}
        label={t('attendance_kpi_checked_in', 'Bugün Giriş')}
        value={`${stats.todayPresent || 0}/${totalEmployees || '-'}`}
        variant="success"
      />
      <KpiCard
        icon={AlertTriangle}
        label={t('attendance_kpi_late_count', 'Geç Kalan')}
        value={stats.late || 0}
        variant="warning"
      />
      <KpiCard
        icon={XCircle}
        label={t('attendance_kpi_absent', 'Eksik/Gelmeyen')}
        value={stats.absent || 0}
        variant="danger"
      />
      <KpiCard
        icon={Clock}
        label={t('attendance_kpi_pending', 'Onay Bekleyen')}
        value={stats.pendingApproval || 0}
        variant="info"
      />
      <KpiCard
        icon={TrendingUp}
        label={t('attendance_kpi_total_hours', 'Toplam Saat')}
        value={`${stats.totalHours || 0}h`}
        variant="primary"
      />
    </div>
  );
};

export default AttendanceDashboard;